<template>
  <section class="promo-code">
    <form class="promo-code__promo" @submit.prevent>
      <div v-if="canShowCoupon" class="promo-code__coupon-wrapper">
        <div
          :class="[
            'promo-code__coupon',
            !isCouponValid && 'promo-code__coupon--invalid',
          ]"
        >
          <span>{{ coupon }}</span>
          <div
            class="promo-code__coupon-close"
            @click.prevent="removeCoupon()"
          ></div>
        </div>
        <div
          :class="[
            'promo-code__coupon-label',
            !isCouponValid && 'promo-code__coupon-label--invalid',
          ]"
        >
          <vue-loaders
            v-if="!canShowCoupon && couponSpinner"
            class="promo-code__promo-input-spinner"
            name="line-spin-fade-loader"
            color="#dfe1e8"
            scale=".3"
          ></vue-loaders>
          {{ couponLabel }}
        </div>
      </div>
      <div
        :class="[
          'promo-code__promo-input-wrapper',
          canShowCoupon && 'promo-code__promo-input-wrapper--hide',
        ]"
      >
        <input
          v-model="coupon"
          class="ine-input promo-code__promo-input"
          type="text"
          placeholder="Enter Promo Code"
          name="coupon"
          @focus="onFocus"
          @blur="onBlur"
        />
        <vue-loaders
          v-if="!canShowCoupon && couponSpinner"
          class="promo-code__promo-input-spinner"
          name="line-spin-fade-loader"
          color="#dfe1e8"
          scale=".3"
        ></vue-loaders>
        <transition name="fade">
          <button
            v-if="showCouponButton"
            name="submitCoupon"
            type="submit"
            :class="[
              'btn',
              'btn--primary',
              'promo-code__btn',
              canShowCoupon && 'promo-code__btn--hide',
            ]"
            :disabled="!coupon"
            @click="addCoupon"
          >
            Apply
          </button>
        </transition>
      </div>
    </form>
  </section>
</template>

<script>
export default {
  name: 'PromoCode',
  props: {
    selectedProduct: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      couponSpinner: false,
      pricingDiscount: '0.00',
      coupon: '',
      couponError: null,
      currentCouponInfo: {},
      isCouponButtonFocused: false,
    }
  },
  computed: {
    isCouponValid() {
      return !this.couponError
    },
    canShowCoupon() {
      return !!this.currentCouponInfo && this.couponError !== null
    },
    couponLabel() {
      let couponLabel = null
      if (this.isCouponValid) {
        couponLabel = `Nice! You're saving $${this.pricingDiscount}`
      } else {
        couponLabel = 'Code invalid'
      }
      return couponLabel
    },
    showCouponButton() {
      return this.isCouponButtonFocused || this.coupon.length > 0
    },
  },
  watch: {
    selectedProduct() {
      if (this.coupon.length > 0) {
        setTimeout(() => {
          this.addCoupon()
        }, 500)
      }
    },
  },
  created() {
    if (this.coupon.length > 0) {
      setTimeout(() => {
        this.addCoupon()
      }, 1000)
    }
  },
  methods: {
    addCoupon() {
      this.couponError = null
      if (this.coupon.length > 0) {
        this.handleCouponInformation()
      }
    },
    removeCoupon() {
      this.coupon = ''
      this.currentCouponInfo = {}
      this.couponError = null
      this.couponDiscount = 0
      this.$emit('set-coupon-code', this.coupon)
    },
    async handleCouponInformation() {
      const couponHasError = () => {
        this.couponError = true
        this.couponSpinner = false
      }
      try {
        this.couponSpinner = true
        const couponInformation = await this.$store.dispatch(
          'subscriptions/getCouponInformation',
          this.coupon
        )
        if (couponInformation) {
          this.currentCouponInfo = couponInformation
          const couponItems = [
            ...couponInformation.items.map(i => i.code),
            ...couponInformation.plans.map(i => i.code),
          ]

          let couponAvailableForDiscount = false
          let totalDiscountToApply = 0

          if (
            couponInformation.discount &&
            couponInformation.discount.percent
          ) {
            if (couponItems.includes(this.selectedProduct.merchant_id)) {
              couponAvailableForDiscount = true
              const { percent } = couponInformation.discount
              const currentItemPrice = this.selectedProduct
                .user_receives_discount
                ? this.selectedProduct.discounted_price_cents
                : this.selectedProduct.price_cents
              totalDiscountToApply += (percent * (currentItemPrice / 100)) / 100
            }
          } else if (
            couponInformation.discount &&
            couponInformation.discount.amount
          ) {
            const { amount } = couponInformation.discount
            if (this.selectedProduct.merchant_id) {
              couponAvailableForDiscount = true
              totalDiscountToApply = amount
            }
          }

          if (couponAvailableForDiscount) {
            this.$emit('set-coupon-code', this.coupon)
            this.$emit('set-coupon-discount', totalDiscountToApply)
            this.pricingDiscount = totalDiscountToApply.toFixed(2)
            this.couponError = false
            this.couponSpinner = false
          } else {
            couponHasError()
          }
        } else {
          couponHasError()
        }
      } catch (error) {
        if (error) this.handleError(error)
        couponHasError()
      }
    },
    handleError(error) {
      console.error('handleError', error)
    },
    onFocus() {
      this.isCouponButtonFocused = true
    },
    onBlur() {
      if (!this.coupon.length) {
        this.isCouponButtonFocused = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.promo-code {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
  flex: 1 0 0;
  align-self: stretch;
  &__type {
    font-family: var(--font-regular);
    color: var(--white);
    font-size: rem(20px);
    line-height: 26px;
    text-align: left;
    font-weight: 700;
    margin-bottom: 10px;
  }
  &__prices-wrapper {
    position: relative;
    margin: 20px 0 0 0;
    @include tablet {
      margin: 50px 0 0 0;
      margin: 0;
    }
  }
  &__promo {
    position: relative;
    font-family: var(--font-regular);
    outline: none;
    border: none;
    margin: 12.5px auto;
    padding: 0;
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    &-input {
      height: 48px;
      width: 100%;
      transition: all 0.5s ease;
      &-wrapper {
        width: 100%;
        display: flex;
        gap: 15px;
        &--hide {
          display: none;
        }
      }
      &-spinner {
        position: absolute;
        right: 100px;
        bottom: -3px;
      }
    }
  }
  &__btn {
    &--hide {
      visibility: hidden;
    }
  }
  &__coupon {
    height: 26px;
    padding: 5px 7px 5px 10px;
    color: var(--white);
    gap: 10px;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--purple);

    &-wrapper {
      display: flex;
      align-items: center;
      width: 100%;
      height: 26px;
      min-width: fit-content;
    }
    &-label {
      color: var(--purple-pale);
      font-size: rem(14);
      line-height: 20px;
      margin-left: 10px;
      text-align: left;
      &--invalid {
        color: var(--red-two);
      }
    }
    &-close {
      cursor: pointer;
      width: 16px;
      height: 24px;
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M6 6L18 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      background-size: 18px 24px;
      background-repeat: no-repeat;
    }
    &--invalid {
      background: var(--red-two);
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
